import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import ConversationList from '../../components/ConversationList';
import MessageBubble from '../../components/MessageBubble';
import ChatInput from '../../components/ChatInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    minHeight: '100vh',
    height: '100vh',
    overflow: 'hidden',
    boxSizing: 'border-box',
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: 'linear-gradient(90deg, #dce4eb, #d7d4cf)',
    display: 'grid',
    gridTemplateColumns: '320px minmax(0, 1fr)',
    gap: 0,
    borderRadius: 14,
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
      height: 'auto',
      minHeight: '100vh',
    },
  },
  sidebar: {
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr)',
    borderRight: '1px solid #dce4eb',
    background: '#fff',
    minHeight: 0,
    borderTopLeftRadius: 14,
    borderBottomLeftRadius: 14,
    '@media (max-width: 900px)': {
      borderRight: 'none',
      borderBottom: '1px solid #dce4eb',
      borderTopLeftRadius: 14,
      borderBottomLeftRadius: 0,
    },
  },
  sidebarHeader: {
    padding: '12px 16px',
    display: 'grid',
    gap: 6,
    background: '#002D55',
    color: '#fff',
    borderBottom: '3px solid #fb741a',
    borderTopLeftRadius: 14,
  },
  sidebarTitle: {
    fontSize: 18,
    fontWeight: 800,
  },
  sidebarSubtitle: {
    fontSize: 13,
    opacity: 0.82,
    lineHeight: 1.5,
  },
  sidebarBody: {
    minHeight: 0,
    overflow: 'auto',
    padding: 16,
  },
  threadPanel: {
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr) auto',
    minHeight: 0,
    background: '#fff',
    borderTopRightRadius: 14,
    borderBottomRightRadius: 14,
  },
  threadHeader: {
    padding: '12px 16px',
    borderBottom: '3px solid #fb741a',
    display: 'grid',
    gap: 4,
    background: '#002D55',
    color: '#fff',
    borderTopRightRadius: 14,
  },
  threadTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 800,
  },
  threadSubtitle: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 13,
    lineHeight: 1.5,
  },
  messageList: {
    minHeight: 0,
    overflowY: 'auto',
    padding: 16,
    background: '#fff',
  },
  emptyState: {
    height: '100%',
    display: 'grid',
    placeItems: 'center',
    color: '#64748B',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  emptyCard: {
    maxWidth: 480,
    border: '1px solid #dce4eb',
    borderRadius: 18,
    padding: theme.spacing(3),
    background: '#fff',
  },
  composerWrap: {
    borderTop: '1px solid #dce4eb',
    background: '#fff',
    borderBottomRightRadius: 14,
  },
  refreshButton: {
    justifySelf: 'start',
    textTransform: 'none',
    borderRadius: 999,
    padding: '6px 14px',
    background: '#fff',
    color: '#002D55',
    border: '1px solid #dce4eb',
    '&:hover': {
      background: '#fff7f1',
      borderColor: '#fb741a',
    },
  },
}));

export default function PatientMessages() {
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const classes = useStyles();
  const [conversations, setConversations] = useState([]);
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  const activeConversation = useMemo(
    () => conversations.find((conversation) => String(conversation.id) === String(activeConversationId)) || null,
    [activeConversationId, conversations]
  );

  const activeMessages = messagesByConversation[activeConversationId] || [];

  const fetchConversations = useCallback(async () => {
    if (!token) {
      setConversations([]);
      setLoadingConversations(false);
      return;
    }

    try {
      setLoadingConversations(true);
      const response = await fetch(`${BACKEND_URL}/api/messaging/conversations/`, {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setConversations([]);
        return;
      }

      const data = await response.json();
      const nextConversations = Array.isArray(data) ? data : [];
      setConversations(nextConversations);

      setActiveConversationId((current) => {
        if (current && nextConversations.some((conversation) => String(conversation.id) === String(current))) {
          return current;
        }

        return nextConversations[0]?.id || null;
      });
    } catch (error) {
      console.error('[PatientMessages] Failed to fetch conversations:', error);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, [token]);

  const fetchMessages = useCallback(async (conversationId) => {
    if (!token || !conversationId) {
      return;
    }

    try {
      setLoadingMessages(true);
      const response = await fetch(`${BACKEND_URL}/api/messaging/messages/${conversationId}/`, {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      setMessagesByConversation((current) => ({
        ...current,
        [conversationId]: Array.isArray(data) ? data : [],
      }));
    } catch (error) {
      console.error('[PatientMessages] Failed to fetch messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, [token]);

  useEffect(() => {
    void fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!activeConversationId) {
      return undefined;
    }

    void fetchMessages(activeConversationId);
    const intervalId = window.setInterval(() => {
      void fetchMessages(activeConversationId);
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeConversationId, fetchMessages]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void fetchConversations();
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchConversations]);

  const handleSelectConversation = async (conversation) => {
    setActiveConversationId(conversation.id);
    await fetchMessages(conversation.id);
  };

  const handleSendMessage = async (content) => {
    if (!token || !activeConversationId || !content.trim()) {
      return;
    }

    try {
      setSendingMessage(true);
      const response = await fetch(`${BACKEND_URL}/api/messaging/messages/${activeConversationId}/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });

      if (response.ok) {
        const newMessage = await response.json();
        setMessagesByConversation((current) => ({
          ...current,
          [activeConversationId]: [...(current[activeConversationId] || []), newMessage],
        }));
        await fetchConversations();
      }
    } catch (error) {
      console.error('[PatientMessages] Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  if (!token) {
    return (
      <Box className={classes.emptyState}>
        <Box className={classes.emptyCard}>
          <Typography style={{ color: '#002D55', fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
            Messages
          </Typography>
          <Typography style={{ color: '#64748B', lineHeight: 1.6 }}>
            Sign in again to view your inbox.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.sidebar}>
        <Box className={classes.sidebarHeader}>
          <Typography className={classes.sidebarTitle}>Inbox</Typography>
          <Typography className={classes.sidebarSubtitle}>
            Your secure inbox with the clinic team.
          </Typography>
          <Button className={classes.refreshButton} onClick={fetchConversations} disabled={loadingConversations}>
            Refresh inbox
          </Button>
        </Box>

        <Box className={classes.sidebarBody}>
          <ConversationList
            conversations={conversations}
            loading={loadingConversations}
            activeConversationId={activeConversationId}
            onSelectConversation={handleSelectConversation}
          />
        </Box>
      </Box>

      <Box className={classes.threadPanel}>
        <Box className={classes.threadHeader}>
          <Typography className={classes.threadTitle}>
            {activeConversation?.other_participant_name || 'Select a conversation'}
          </Typography>
          <Typography className={classes.threadSubtitle}>
            {activeConversation
              ? 'Read the latest message and reply below.'
              : 'Choose a conversation from the inbox to load the thread.'}
          </Typography>
        </Box>

        <Box className={classes.messageList}>
          {loadingMessages && !activeMessages.length ? (
            <Box style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
              <CircularProgress size={28} />
            </Box>
          ) : activeConversation ? (
            activeMessages.length > 0 ? (
              activeMessages.map((message) => {
                const senderId = message.sender?.id || message.sender;
                const senderLabel = message.sender_name || message.senderName || message.sender_email || 'Care Provider';
                const initialSource = senderLabel.split('@')[0].trim();
                const senderInitials = initialSource ? initialSource[0].toUpperCase() : 'U';

                return (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isSender={String(senderId) === String(user?.id)}
                    senderName={senderLabel}
                    senderInitials={senderInitials}
                  />
                );
              })
            ) : (
              <Box className={classes.emptyState}>
                <Box className={classes.emptyCard}>
                  <Typography style={{ color: '#002D55', fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
                    No messages yet
                  </Typography>
                  <Typography style={{ color: '#64748B', lineHeight: 1.6 }}>
                    This conversation is empty. Send the first message below.
                  </Typography>
                </Box>
              </Box>
            )
          ) : (
            <Box className={classes.emptyState}>
              <Box className={classes.emptyCard}>
                <Typography style={{ color: '#002D55', fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
                  No conversation selected
                </Typography>
                <Typography style={{ color: '#64748B', lineHeight: 1.6 }}>
                  Pick a conversation from the inbox to view the message thread.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box className={classes.composerWrap}>
          <ChatInput
            onSendMessage={handleSendMessage}
            disabled={!activeConversation || sendingMessage}
            loading={sendingMessage}
          />
        </Box>
      </Box>
    </Box>
  );
}